// feat_347 — Tab "Negocio" de /Settings.
// feat_353 — Fase 2, paso 3: PRIMER tab grande restyleado con el PATRÓN VISUAL
// CANÓNICO de feat_351 (`../components/SettingsPattern.js` + CLAUDE.md, sección
// "Patrón visual de los tabs de /Settings"). La referencia chica sigue siendo
// TabPermisos.js; este es el que prueba que el patrón aguanta un tab con
// campos de texto.
//
// 🔴 REGLA #1 DE ESTA PANTALLA: los 8 TabPanel quedan SIEMPRE MONTADOS (el shell
// los oculta con `hidden`, nunca los desmonta). 26 campos de texto guardan con
// `onBlur`; si al cambiar de tab React desmontara el input, el onBlur NO dispara
// y el usuario pierde lo tipeado sin ningún aviso. **10 de esos 26 están en este
// archivo** — acá la regla no es teórica. NO convertir esto en render condicional
// ni en lazy, y nada de Collapse/Accordion que desmonte contenido.
//
// ⚠ `id="business-hours-section"` ES UN ANCLA VIVA. El Alert de E1 (tab
// automatizaciones) tiene un botón que llama
// `goToTab("negocio", "business-hours-section")`. Si el id se pierde, el
// `if (el)` de goToTab se traga el null y el botón queda MUDO EN SILENCIO. En
// feat_353 el id se mudó del <Typography> suelto al <SectionHeader> de la misma
// sección — sigue siendo el primer elemento de su card, que es lo que uno quiere
// arriba del viewport al saltar. Probado en browser.
//
// Todo el estado vive en el padre (`Settings/index.js`) y llega por `ctx`.
// Las 15 keys y sus 10 `onBlur` NO se tocaron: el restyle mueve markup, nunca el
// mecanismo de guardado.

import React from "react";

import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../../translate/i18n.js";
import WeeklySchedulePicker from "../../../components/WeeklySchedulePicker";
import HolidaysEditor from "../../../components/HolidaysEditor";
import {
	TabHeader,
	SettingsCard,
	SectionHeader,
	ToggleRow,
	FieldGrid,
	FieldCell,
} from "../components/SettingsPattern";

// Punto 5 del patrón: `variant="outlined"` SIEMPRE. Para los <select> nativos se
// usa `<TextField select SelectProps={{ native: true }}>` en vez de un <Select>
// suelto envuelto en <FormControl>: el TextField arma solo el hueco (notch) del
// contorno para el label, así que no hay que medir `labelWidth` a mano.
// ⚠ `shrink: true` NO es opcional acá: un select nativo SIEMPRE muestra un valor,
// y sin shrink el label se monta encima de ese valor. Es exactamente el defecto
// que CLAUDE.md ya tenía anotado para "Días de reapertura".
const selectProps = {
	select: true,
	SelectProps: { native: true },
	InputLabelProps: { shrink: true },
	variant: "outlined",
	size: "small",
	fullWidth: true,
};

// Campo de texto del tab: outlined + small + fullWidth dentro de su celda de la
// grilla. El ancho lo decide la celda (`<FieldCell>` o `<FieldCell full>`), no el
// campo — punto 6 del patrón.
const textProps = {
	variant: "outlined",
	size: "small",
	fullWidth: true,
};

const TabNegocio = ({ ctx }) => {
	const {
		saveSetting,
		businessName, setBusinessName,
		sede1, setSede1,
		sede2, setSede2,
		assistantName, setAssistantName,
		language, setLanguage,
		tone, setTone,
		always247, setAlways247,
		businessHours, setBusinessHours,
		businessAddress, setBusinessAddress,
		businessPhone, setBusinessPhone,
		businessWhatsapp, setBusinessWhatsapp,
		businessEmail, setBusinessEmail,
		businessLocationLink, setBusinessLocationLink,
		businessHoursText, setBusinessHoursText,
		businessTimezone, setBusinessTimezone,
	} = ctx;

	return (
		<>
			<TabHeader title={i18n.t("settingsExtra.tabNegocio")} />

			{/* ---------- 1. Identidad del Asistente ---------- */}
			<SettingsCard>
				<SectionHeader
					title={i18n.t("settingsExtra.identidadAsistente")}
					description={i18n.t("settingsExtra.negocioIdentidadHelp")}
				/>
				<FieldGrid>
					<FieldCell>
						<TextField
							{...textProps}
							label={i18n.t("settingsExtra.nombreNegocio")}
							placeholder={i18n.t("settingsExtra.phNombreNegocio")}
							value={businessName}
							onChange={e => setBusinessName(e.target.value)}
							onBlur={() => saveSetting("businessName", businessName)}
						/>
					</FieldCell>
					<FieldCell>
						<TextField
							{...textProps}
							label={i18n.t("settingsExtra.nombreAsistente")}
							placeholder={i18n.t("settingsExtra.phNombreAsistente")}
							value={assistantName}
							onChange={e => setAssistantName(e.target.value)}
							onBlur={() => saveSetting("assistantName", assistantName)}
						/>
					</FieldCell>
					<FieldCell>
						<TextField
							{...textProps}
							label={i18n.t("settingsExtra.negocioSedeLabel1")}
							placeholder={i18n.t("settingsExtra.negocioSedePh1")}
							value={sede1}
							onChange={e => setSede1(e.target.value)}
							onBlur={() => saveSetting("sede1", sede1)}
						/>
					</FieldCell>
					<FieldCell>
						<TextField
							{...textProps}
							label={i18n.t("settingsExtra.negocioSedeLabel2")}
							placeholder={i18n.t("settingsExtra.negocioSedePh2")}
							value={sede2}
							onChange={e => setSede2(e.target.value)}
							onBlur={() => saveSetting("sede2", sede2)}
						/>
					</FieldCell>
					<FieldCell>
						<TextField
							{...selectProps}
							label={i18n.t("settingsExtra.idioma")}
							value={language}
							onChange={e => { setLanguage(e.target.value); saveSetting("language", e.target.value); }}
						>
							<option value="es">Español</option>
							<option value="en">English</option>
							<option value="pt">Português</option>
							<option value="it">Italiano</option>
						</TextField>
					</FieldCell>
					<FieldCell>
						<TextField
							{...selectProps}
							label={i18n.t("settingsExtra.tonoAsistente")}
							value={tone}
							onChange={e => { setTone(e.target.value); saveSetting("tone", e.target.value); }}
						>
							<option value="informal">Informal</option>
							<option value="formal">Formal</option>
						</TextField>
					</FieldCell>
				</FieldGrid>
			</SettingsCard>

			{/* ---------- 2. Horario comercial (feat_193) ----------
			    Config base referenciada por los automatismos. El id del
			    SectionHeader es el ancla de goToTab — ver el aviso de arriba. */}
			<SettingsCard>
				<SectionHeader
					id="business-hours-section"
					title={i18n.t("settings.businessHours.title")}
					description={i18n.t("settingsExtra.negocioHorarioHelp")}
				/>
				<ToggleRow
					id="setting-always247"
					label={i18n.t("settings.businessHours.always247Label")}
					help={i18n.t("settingsExtra.negocioAlways247Help")}
					tooltip={i18n.t("settings.businessHours.always247Helper")}
					checked={always247}
					onChange={async (e) => {
						setAlways247(e.target.checked);
						await saveSetting("always247", e.target.checked ? "true" : "false");
					}}
				/>
				{/* Este condicional YA existía antes de feat_353 y se conserva tal
				    cual: el WeeklySchedulePicker guarda por `onCommit`, no por
				    `onBlur`, así que desmontarlo no pierde nada tipeado. NO
				    convertir ningún campo con onBlur en algo condicional. */}
				{!always247 && (
					<div style={{ marginTop: 12 }}>
						<Typography variant="body2" gutterBottom>
							{i18n.t("settings.businessHours.scheduleLabel")}
						</Typography>
						<Typography variant="caption" color="textSecondary" display="block" gutterBottom>
							{i18n.t("settings.businessHours.scheduleHelper")}
						</Typography>
						<WeeklySchedulePicker
							value={businessHours}
							onChange={setBusinessHours}
							onCommit={next => saveSetting("businessHours", JSON.stringify(next))}
						/>
					</div>
				)}
			</SettingsCard>

			{/* ---------- 3. Datos del negocio (feat_289) ----------
			    Es el payload que la IA recibe por webhook en cada conversación. */}
			<SettingsCard>
				<SectionHeader
					title={i18n.t("settings.businessInfo.title")}
					description={i18n.t("settings.businessInfo.subtitle")}
				/>
				<FieldGrid>
					{/* Ancho completo: es multiline (punto 6 del patrón). */}
					<FieldCell full>
						<TextField
							{...textProps}
							label={i18n.t("settings.businessInfo.address")}
							value={businessAddress}
							onChange={e => setBusinessAddress(e.target.value)}
							onBlur={() => saveSetting("businessAddress", businessAddress)}
							multiline
							rows={2}
						/>
					</FieldCell>
					<FieldCell>
						<TextField
							{...textProps}
							label={i18n.t("settings.businessInfo.phone")}
							value={businessPhone}
							onChange={e => setBusinessPhone(e.target.value)}
							onBlur={() => saveSetting("businessPhone", businessPhone)}
						/>
					</FieldCell>
					<FieldCell>
						<TextField
							{...textProps}
							label={i18n.t("settings.businessInfo.whatsapp")}
							value={businessWhatsapp}
							onChange={e => setBusinessWhatsapp(e.target.value)}
							onBlur={() => saveSetting("businessWhatsapp", businessWhatsapp)}
						/>
					</FieldCell>
					<FieldCell>
						<TextField
							{...textProps}
							label={i18n.t("settings.businessInfo.email")}
							value={businessEmail}
							onChange={e => setBusinessEmail(e.target.value)}
							onBlur={() => saveSetting("businessEmail", businessEmail)}
						/>
					</FieldCell>
					{/* feat_353 — ÚNICO cambio de orden del paso: la zona horaria
					    estaba al final y subió acá, al lado del email. Es para que
					    la grilla de 2 columnas no quede con media fila vacía; el
					    campo, su key y su guardado son los mismos. */}
					<FieldCell>
						<TextField
							{...selectProps}
							label={i18n.t("settings.businessInfo.timezone")}
							value={businessTimezone}
							onChange={e => { setBusinessTimezone(e.target.value); saveSetting("businessTimezone", e.target.value); }}
						>
							<option value="America/Argentina/Cordoba">Argentina - Cordoba / Buenos Aires</option>
							<option value="America/Argentina/Salta">Argentina - Salta</option>
							<option value="America/Argentina/Mendoza">Argentina - Mendoza</option>
							<option value="America/Montevideo">Uruguay - Montevideo</option>
							<option value="America/Santiago">Chile - Santiago</option>
							<option value="America/Sao_Paulo">Brasil - Sao Paulo</option>
							<option value="America/Mexico_City">Mexico - CDMX</option>
							<option value="America/Bogota">Colombia - Bogota</option>
							<option value="America/Lima">Peru - Lima</option>
							<option value="Europe/Rome">Italia - Roma</option>
							<option value="Europe/Madrid">Espana - Madrid</option>
						</TextField>
					</FieldCell>
					{/* Ancho completo: es una URL (punto 6 del patrón). */}
					<FieldCell full>
						<TextField
							{...textProps}
							label={i18n.t("settings.businessInfo.locationLink")}
							value={businessLocationLink}
							onChange={e => setBusinessLocationLink(e.target.value)}
							onBlur={() => saveSetting("businessLocationLink", businessLocationLink)}
						/>
					</FieldCell>
					<FieldCell full>
						<TextField
							{...textProps}
							label={i18n.t("settings.businessInfo.hoursText")}
							value={businessHoursText}
							onChange={e => setBusinessHoursText(e.target.value)}
							onBlur={() => saveSetting("businessHoursText", businessHoursText)}
							multiline
							rows={2}
							helperText={i18n.t("settings.businessInfo.hoursTextHelp")}
						/>
					</FieldCell>
				</FieldGrid>
			</SettingsCard>

			{/* ---------- 4. Feriados (feat_289) ---------- */}
			<SettingsCard>
				<SectionHeader
					title={i18n.t("holidays.title")}
					description={i18n.t("settingsExtra.negocioFeriadosHelp")}
				/>
				<HolidaysEditor />
			</SettingsCard>
		</>
	);
};

export default TabNegocio;
